import React, { useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import {
    ResponsiveContainer,
    LineChart,
    Line,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
    Legend,
    ReferenceLine,
} from 'recharts';
import {
    timeSeriesXAxis,
    colors,
    lineGraphDotSize,
    lineGridStyle,
} from '../../../../shared/constants/constants';
import CustomTooltip from '../../CustomTooltip';
import {
    getXAxisLabel,
    getYAxisLabel,
    getBiaxialChartMargin,
    getFormattedValue,
} from '../../../dashboardUtils';

// eslint-disable-next-line complexity
function TimeSeriesBiaxialLineGraph({
    chartProperties,
    dataPoints,
    dateFormatter,
    leftDataFormatter,
    leftDataLabels,
    legendFormatter,
    rightDataFormatter,
    rightDataLabels,
    thresholds,
    xAxisLabel,
    yAxisLabelLeft,
    yAxisLabelRight,
}) {
    const [activeIndex, setActiveIndex] = useState(-1);
    const {
        colorPalette = colors,
        lineChartProperties,
        lineProperties,
        legendProperties,
        referenceLineProperties,
        responsiveContainerProperties,
        showLegend,
        showSinglePointTooltip,
        leftSuffix = '',
        rightSuffix = '',
        timeFormat,
        tooltipProperties,
        xAxisProperties,
        yAxisProperties,
    } = chartProperties;

    const leftAxisTickFormatter = (val) =>
        getFormattedValue(val, leftSuffix, leftDataFormatter, timeFormat);
    const rightAxisTickFormatter = (val) =>
        getFormattedValue(val, rightSuffix, rightDataFormatter, timeFormat);

    const chartMargin = getBiaxialChartMargin(
        dataPoints,
        timeSeriesXAxis,
        leftDataLabels,
        rightDataLabels,
        leftAxisTickFormatter,
        rightAxisTickFormatter
    );

    return (
        <ResponsiveContainer {...responsiveContainerProperties}>
            <LineChart {...lineChartProperties} data={dataPoints}>
                <CartesianGrid strokeDasharray={lineGridStyle} />
                <XAxis
                    {...xAxisProperties}
                    dataKey={timeSeriesXAxis}
                    tickFormatter={dateFormatter}
                    label={getXAxisLabel(xAxisLabel, xAxisProperties)}
                />
                <YAxis
                    {...yAxisProperties}
                    yAxisId="left"
                    label={getYAxisLabel(yAxisLabelLeft)}
                    tickFormatter={leftAxisTickFormatter}
                    width={chartMargin.left}
                />
                <YAxis
                    {...yAxisProperties}
                    yAxisId="right"
                    orientation="right"
                    label={getYAxisLabel(yAxisLabelRight, false)}
                    tickFormatter={rightAxisTickFormatter}
                    width={chartMargin.right}
                />
                <Tooltip
                    {...tooltipProperties}
                    cursor={dataPoints.length !== 0}
                    content={
                        <CustomTooltip
                            activeIndex={activeIndex}
                            axisFormatter={dateFormatter}
                            axisKey={timeSeriesXAxis}
                            dataFormat={timeFormat}
                            dataFormatter={
                                leftDataLabels[activeIndex]
                                    ? leftDataFormatter
                                    : rightDataFormatter
                            }
                            legendFormatter={legendFormatter}
                            showSinglePoint={showSinglePointTooltip}
                            suffix={
                                leftDataLabels[activeIndex]
                                    ? leftSuffix
                                    : rightSuffix
                            }
                            thresholds={thresholds}
                        />
                    }
                />
                {showLegend && (
                    <Legend {...legendProperties} formatter={legendFormatter} />
                )}
                {leftDataLabels.map((dataLabel, index) => (
                    <Line
                        {...lineProperties}
                        key={dataLabel}
                        yAxisId="left"
                        dataKey={dataLabel}
                        stroke={colorPalette[index % colorPalette.length]}
                        activeDot={{
                            r: lineGraphDotSize,
                            onMouseOver: () => setActiveIndex(index),
                            onMouseOut: () => setActiveIndex(-1),
                            onFocus: () => setActiveIndex(index),
                            onBlur: () => setActiveIndex(-1),
                        }}
                    />
                ))}
                {rightDataLabels.map((dataLabel, index) => (
                    <Line
                        {...lineProperties}
                        key={dataLabel}
                        yAxisId="right"
                        dataKey={dataLabel}
                        stroke={
                            colorPalette[
                                (leftDataLabels.length + index) %
                                    colorPalette.length
                            ]
                        }
                        activeDot={{
                            r: lineGraphDotSize,
                            onMouseOver: () =>
                                setActiveIndex(leftDataLabels.length + index),
                            onMouseOut: () => setActiveIndex(-1),
                            onFocus: () =>
                                setActiveIndex(leftDataLabels.length + index),
                            onBlur: () => setActiveIndex(-1),
                        }}
                    />
                ))}
                {thresholds.map((threshold) => (
                    <ReferenceLine
                        key={`threshold-${threshold.id}`}
                        {...referenceLineProperties}
                        y={threshold.value}
                        stroke={threshold.lineColor}
                    />
                ))}
            </LineChart>
        </ResponsiveContainer>
    );
}

TimeSeriesBiaxialLineGraph.propTypes = {
    chartProperties: PropTypes.instanceOf(Object).isRequired,
    dataPoints: PropTypes.instanceOf(Array),
    dateFormatter: PropTypes.func.isRequired,
    leftDataFormatter: PropTypes.func,
    leftDataLabels: PropTypes.instanceOf(Array),
    legendFormatter: PropTypes.func.isRequired,
    rightDataFormatter: PropTypes.func,
    rightDataLabels: PropTypes.instanceOf(Array),
    thresholds: PropTypes.instanceOf(Array).isRequired,
    xAxisLabel: PropTypes.string,
    yAxisLabelLeft: PropTypes.string,
    yAxisLabelRight: PropTypes.string,
};

TimeSeriesBiaxialLineGraph.defaultProps = {
    dataPoints: [],
    leftDataLabels: [],
    rightDataLabels: [],
    xAxisLabel: null,
    yAxisLabelLeft: null,
    yAxisLabelRight: null,
};

export default React.memo(
    TimeSeriesBiaxialLineGraph,
    (prevProps, nextProps) =>
        _.isEqual(prevProps.dataPoints, nextProps.dataPoints) &&
        _.isEqual(prevProps.thresholds, nextProps.thresholds)
);
